import React, { useState } from "react";
import Select, { MultiValue } from "react-select";
import { Modal } from "../../common/Modal/Modal";
import { styled } from "../../../stitches.config";
import { Box, FlexBox, Option } from "../../primitives";
import {
  LabelStyled,
  PrimaryButton,
  SecondaryButton,
  StyledInput,
  StyledSelect,
  TitleStyle,
} from "../../common/common.style";
import {
  CARDTRADER_SELLER_COUNTRY_CODE,
  CardTraderStrategyEnum,
} from "magic-shared-types";
import { InfoTooltip } from "../../common/InfoTooltip/InfoTooltip";
import { CheckBox } from "../../common/CheckBox/CheckBox";
import { Group, LabelGroup, Section } from "../modalCreateStrategy.styled";
import { useCreateCardTraderPriceStrategyMutation } from "../../../services/myServer/priceStrategy";
import toast from "react-hot-toast";
import { LoadingOverlay } from "../../common/LoadingOverley/LoadingOverley";

type OptionType = {
  value: string;
  label: string;
};

const Wrapper = styled(Box, {
  padding: 20,
});

interface ModalCreateStrategyCardTraderProps {
  setIsOpenModalCreateStrategyPrice: (arg1: boolean) => void;
  refetch: () => void;
}

export const ModalCreateStrategyCardTrader = ({
  setIsOpenModalCreateStrategyPrice,
  refetch,
}: ModalCreateStrategyCardTraderProps) => {
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<OptionType[]>([]);
  const [minValue, setMinValue] = useState(0.2);
  const [minFeedback, setMinFeedback] = useState(0);
  const [isSameLanguage, setIsSameLanguage] = useState(false);
  const [isExSameAsNm, setIsExSameAsNm] = useState(false);
  const [isOnlyFastSeller, setIsOnlyFastSeller] = useState(false);
  const [isOnlyZeroSeller, setIsOnlyZeroSeller] = useState(false);
  const [strategyName, setStrategyName] = useState("");
  const [excludeSellers, setExcludeSellers] = useState("");
  const [strategy, setStrategy] = useState<CardTraderStrategyEnum>(
    CardTraderStrategyEnum.FIRST_PRICE
  );
  const [createCardTraderPriceStrategy] =
    useCreateCardTraderPriceStrategyMutation();
  const mappedLocations = CARDTRADER_SELLER_COUNTRY_CODE.map(
    (country: { value: string; name: string }) => ({
      value: country.value,
      label: country.name,
    })
  );

  const handleChange = (value: MultiValue<OptionType>) => {
    setSelected([...value] as OptionType[]);
  };

  const onClickCreate = async () => {
    setLoading(true);
    try {
      await createCardTraderPriceStrategy({
        name: strategyName,
        location: selected.map((opt) => opt.value).join(","),
        ex_same_as_nm: isExSameAsNm,
        feedback: minFeedback,
        min_price: minValue,
        only_fast_seller: isOnlyFastSeller,
        only_zero_seller: isOnlyZeroSeller,
        same_language: isSameLanguage,
        exclude_sellers: excludeSellers,
        strategy,
      });
      setIsOpenModalCreateStrategyPrice(false);
      toast.success("Strategy saved succesfully");
      refetch();
    } catch (e) {
      toast.error("Error trying to save strategy");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading && <LoadingOverlay />}
      <Modal onClose={() => setIsOpenModalCreateStrategyPrice(false)}>
        <Wrapper css={{ height: "450px" }}>
          <TitleStyle>Create a new cardtrader strategy</TitleStyle>
          <Section>
            <Group>
              <LabelGroup>
                <LabelStyled>Strategy Name</LabelStyled>
                <InfoTooltip content="Your strategy name" />
              </LabelGroup>
              <StyledInput
                type="text"
                value={strategyName}
                onChange={(e) => setStrategyName(e.target.value)}
              />
            </Group>
            <Group>
              <LabelGroup>
                <LabelStyled>Seller Location</LabelStyled>
                <InfoTooltip content="Select one or multiple countries where the seller is located. This helps narrow down price comparisons to specific geographic regions." />
              </LabelGroup>
              <Select
                options={mappedLocations}
                isMulti
                value={selected}
                onChange={handleChange}
                placeholder="Choose seller location..."
                styles={{
                  container: (base) => ({
                    ...base,
                    width: 300,
                  }),
                }}
              />
            </Group>
          </Section>

          <Section>
            <Group>
              <LabelGroup>
                <LabelStyled>Strategy</LabelStyled>
                <InfoTooltip content="Select the pricing strategy: FIRST_PRICE (lowest price) or AVERAGE (average price from sellers)." />
              </LabelGroup>
              <StyledSelect
                value={strategy}
                onChange={(e) =>
                  setStrategy(e.target.value as CardTraderStrategyEnum)
                }
              >
                <Option value={CardTraderStrategyEnum.FIRST_PRICE}>
                  {CardTraderStrategyEnum.FIRST_PRICE}
                </Option>
                <Option value={CardTraderStrategyEnum.AVARAGE}>
                  {CardTraderStrategyEnum.AVARAGE}
                </Option>
              </StyledSelect>
            </Group>
            <Group>
              <LabelGroup>
                <LabelStyled>Minimum Price</LabelStyled>
                <InfoTooltip content="Set the minimum price threshold for items to be included, preventing very low offers from skewing data." />
              </LabelGroup>
              <StyledInput
                type="number"
                value={minValue}
                onChange={(e) => setMinValue(+e.target.value)}
                onKeyDown={(e) => {
                  if (["e", "E", "+", "-"].includes(e.key)) {
                    e.preventDefault();
                  }
                }}
              />
            </Group>

            <Group>
              <LabelGroup>
                <LabelStyled>Minimum Feedback</LabelStyled>
                <InfoTooltip content="Specify the minimum seller feedback score to ensure reliable sellers are considered." />
              </LabelGroup>
              <StyledInput
                type="number"
                value={minFeedback}
                onChange={(e) => setMinFeedback(+e.target.value)}
                onKeyDown={(e) => {
                  if (["e", "E", "+", "-"].includes(e.key)) {
                    e.preventDefault();
                  }
                }}
              />
            </Group>
            <Group>
              <LabelGroup>
                <LabelStyled>
                  Exclude sellers name (separate them by commas)
                </LabelStyled>
                <InfoTooltip content="Enter seller usernames to exclude, separated by commas (e.g., seller1,seller2). No spaces." />
              </LabelGroup>
              <StyledInput
                type="text"
                placeholder="seller1,seller2,seller3......"
                value={excludeSellers}
                onChange={(e) => setExcludeSellers(e.target.value)}
              />
            </Group>
          </Section>

          <Section>
            <FlexBox css={{ alignItems: "center", gap: 10 }}>
              <LabelStyled>Only same language</LabelStyled>
              <InfoTooltip content="Limit comparisons to sellers offering cards in the same language as your target card." />
              <CheckBox
                checked={isSameLanguage}
                setChecked={(checked: boolean) => {
                  setIsSameLanguage(checked);
                }}
              />
            </FlexBox>

            <FlexBox css={{ alignItems: "center", gap: 10 }}>
              <LabelStyled>NM same as EX</LabelStyled>
              <InfoTooltip content="Treat Near Mint (NM) condition the same as Excellent (EX) in price comparison." />
              <CheckBox
                checked={isExSameAsNm}
                setChecked={(checked: boolean) => {
                  setIsExSameAsNm(checked);
                }}
              />
            </FlexBox>

            <FlexBox css={{ alignItems: "center", gap: 10 }}>
              <LabelStyled>Only Cardtrader Zero sellers</LabelStyled>
              <InfoTooltip content="Limit results to sellers using CardTrader Zero, a service that simplifies and centralizes shipments." />
              <CheckBox
                checked={isOnlyZeroSeller}
                setChecked={(checked: boolean) => {
                  setIsOnlyZeroSeller(checked);
                }}
              />
            </FlexBox>

            <FlexBox css={{ alignItems: "center", gap: 10 }}>
              <LabelStyled>Only Fast seller</LabelStyled>
              <InfoTooltip content="Include only sellers who usually process and ship orders quickly." />
              <CheckBox
                checked={isOnlyFastSeller}
                setChecked={(checked: boolean) => {
                  setIsOnlyFastSeller(checked);
                }}
              />
            </FlexBox>
          </Section>
          <FlexBox css={{ justifyContent: "center", marginTop: 40, gap: 20 }}>
            <PrimaryButton isBig css={{ width: 140 }} onClick={onClickCreate}>
              CREATE STRATEGY
            </PrimaryButton>
            <SecondaryButton
              isBig
              css={{ width: 140 }}
              onClick={() => setIsOpenModalCreateStrategyPrice(false)}
            >
              CLOSE
            </SecondaryButton>
          </FlexBox>
        </Wrapper>
      </Modal>
    </>
  );
};
